// frontend/src/components/customer/HomePage2.jsx
// Luxury editorial landing page — alternate home page design.
// Wraps inside the existing Layout (nav + footer are NOT duplicated here).
// All dynamic data comes from AppContext or the site API.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context';
import ApiService from '../../services/index.js';
import { resolveUploadUrl } from '../../utils';
import toast from 'react-hot-toast';

// Images (same import pattern as HomePage.jsx)
import hero1        from '../../assets/images/hero1.jpg';
import hero2        from '../../assets/images/hero2.jpg';
import hero3        from '../../assets/images/hero3.jpg';
import aboutImg     from '../../assets/images/about.jpg';
import dish1        from '../../assets/images/dish-4.jpg';
import dish2        from '../../assets/images/dish-6.jpg';
import dish3        from '../../assets/images/dish-7.jpg';
import gallery1     from '../../assets/images/gallery-1.jpg';
import gallery2     from '../../assets/images/gallery-2.jpg';
import gallery3     from '../../assets/images/gallery-3.jpg';
import gallery4     from '../../assets/images/gallery-4.jpg';
import gallery5     from '../../assets/images/gallery-5.jpg';
import testimonial1 from '../../assets/images/testimonial-1.jpg';
import testimonial2 from '../../assets/images/testimonial-2.jpg';
import testimonial3 from '../../assets/images/testimonial-3.jpg';

// Static content (real data, not placeholders)
const HERO_IMAGES = [hero1, hero2, hero3];

const TICKER_ITEMS = [
  'Fine Dining','Mountain Views','Swat Valley','Private Events',
  'Authentic Cuisine','Halal Certified','Open Kitchen','Free Parking',
  'Family Friendly','5-Star Experience',
];

const DINING_CARDS = [
  { img: dish1, cat: 'Signature', title: "Chef's Tasting Menu",  desc: 'A journey through regional flavours, carefully reimagined.'              },
  { img: dish2, cat: 'Grill',     title: 'Mountain Grill',        desc: 'Slow-cooked meats over open fire, served with fresh mountain herbs.'   },
  { img: dish3, cat: 'Heritage',  title: 'Pakhtun Heritage',      desc: 'Authentic recipes from the Swat Valley, served with pride.'            },
];

const TESTIMONIALS = [
  { name:'Farhan Khan', location:'Islamabad', rating:5, img:testimonial1, comment:'Absolutely stunning resort! The food was incredible and the mountain views are breathtaking. A must-visit destination in Pakistan.' },
  { name:'Sohail Ayub', location:'Swat',      rating:5, img:testimonial2, comment:'Perfect getaway destination. The staff was extremely hospitable and the facilities are top-notch. The kind of place you return to again and again.' },
  { name:'Maaz Khan',   location:'Lahore',    rating:5, img:testimonial3, comment:'Best resort experience in Pakistan. The location in Swat is magical and the service is exceptional. The dining experience alone is worth the journey.' },
];

const GALLERY_IMAGES = [
  { src:gallery1, wide:true },
  { src:gallery2 },
  { src:gallery3 },
  { src:gallery4 },
  { src:gallery5 },
  { src:hero1    },
  { src:aboutImg },
];

// Helper: format today's operating hours as a readable string
const formatHours = (operatingHours) => {
  if (!operatingHours) return 'Daily 7:00 AM – 11:00 PM';
  const today = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];
  const h = operatingHours[today];
  if (!h || h.isOpen === false) return 'Closed today';
  const fmt = (t) => {
    if (!t) return '';
    const [hh, mm] = t.split(':');
    const d = new Date(); d.setHours(+hh, +mm);
    return d.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit', hour12:true });
  };
  return `Open today: ${fmt(h.open)} – ${fmt(h.close)}`;
};

const HomePage2 = () => {
  const { restaurantInfo, operatingHours, isRestaurantCurrentlyOpen } = useApp();

  // Hero slideshow
  const [slideIndex, setSlideIndex] = useState(0);
  const slideTimer = useRef(null);
  const showcaseTouchRef = useRef(null);

  const goToSlide = useCallback((i) => {
    setSlideIndex((i + HERO_IMAGES.length) % HERO_IMAGES.length);
  }, []);

  const restartTimer = useCallback(() => {
    clearInterval(slideTimer.current);
    slideTimer.current = setInterval(
      () => setSlideIndex(p => (p + 1) % HERO_IMAGES.length),
      5000
    );
  }, []);

  useEffect(() => { restartTimer(); return () => clearInterval(slideTimer.current); }, [restartTimer]);

  // Gallery showcase — state + auto-advance
  const [activeGallery, setActiveGallery] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActiveGallery(g => (g + 1) % GALLERY_IMAGES.length), 3500);
    return () => clearInterval(t);
  }, []);

  // Team from site API
  const [team, setTeam]           = useState([]);
  const [teamLoaded, setTeamLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    ApiService.get('/site/team')
      .then(data => { if (active && data?.success) setTeam(data.data || []); })
      .catch(() => {})
      .finally(() => { if (active) setTeamLoaded(true); });
    return () => { active = false; };
  }, []);

  // Reservation quick-form
  const [resForm, setResForm]     = useState({ name:'', email:'', phone:'', date:'', time:'', guests:'', occasion:'' });
  const [resLoading, setResLoading] = useState(false);

  const patchForm = (field) => (e) => setResForm(p => ({ ...p, [field]: e.target.value }));

  const handleResSubmit = async (e) => {
    e.preventDefault();
    setResLoading(true);
    try {
      await ApiService.createReservation({
        name:            resForm.name,
        email:           resForm.email,
        phone:           resForm.phone,
        date:            resForm.date,
        time:            resForm.time,
        guests:          parseInt(resForm.guests) || 2,
        specialRequests: resForm.occasion ? `Occasion: ${resForm.occasion}` : '',
      });
      toast.success('Reservation request sent! We will contact you shortly.');
      setResForm({ name:'', email:'', phone:'', date:'', time:'', guests:'', occasion:'' });
    } catch (err) {
      toast.error(err?.message || 'Could not send reservation. Please call us directly.');
    } finally {
      setResLoading(false);
    }
  };

  // Inject Cormorant Garamond once per page load (no FOUC on repeat visits)
  useEffect(() => {
    const id = 'hp2-gfont';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id  = id; link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap';
    document.head.appendChild(link);
  }, []);

  // Scroll-reveal (re-observe whenever team cards arrive)
  useEffect(() => {
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('hp2-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.hp2-reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [teamLoaded]);

  // Section snap-scroll — one wheel gesture jumps to the next/previous section.
  // A taller-than-screen section (gallery, reservation form) still scrolls
  // freely inside; the jump only kicks in once you reach that section's edge.
  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) return; // touch: native scroll
    const root = document.querySelector('.hp2');
    if (!root) return;
    const sections = [...root.children].filter(c => c.tagName !== 'STYLE');
    if (sections.length < 2) return;

    const navOffset = () => (window.innerWidth >= 768 ? 80 : 64);
    let locked = false;
    let timer = null;

    const currentIndex = () => {
      const line = navOffset() + 2;
      for (let i = 0; i < sections.length; i++) {
        const r = sections[i].getBoundingClientRect();
        if (r.top <= line && r.bottom > line) return i;
      }
      return window.scrollY < 10 ? 0 : sections.length - 1;
    };

    const scrollToSection = (i) => {
      locked = true;
      const top = sections[i].getBoundingClientRect().top + window.scrollY
                - (i === 0 ? 0 : navOffset());
      window.scrollTo({ top, behavior: 'smooth' });
      clearTimeout(timer);
      timer = setTimeout(() => { locked = false; }, 800);
    };

    const onWheel = (e) => {
      if (e.ctrlKey) return;                       // pinch-zoom
      if (locked) { e.preventDefault(); return; }
      const dir = e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0;
      if (!dir) return;
      const idx = currentIndex();
      const r = sections[idx].getBoundingClientRect();
      // tall section not yet scrolled through -> let it scroll natively
      if (dir > 0 && r.bottom > window.innerHeight + 2) return;
      if (dir < 0 && r.top < navOffset() - 2) return;
      const next = idx + dir;
      if (next < 0 || next >= sections.length) return; // boundary -> native (footer)
      e.preventDefault();
      scrollToSection(next);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => { window.removeEventListener('wheel', onWheel); clearTimeout(timer); };
  }, []);

  // WhatsApp URL — prefer restaurantInfo.whatsapp, fall back to phone
  const waPhone = (restaurantInfo?.whatsapp || restaurantInfo?.phone || '')
    .toString().replace(/[^\d]/g, '');
  const waUrl = waPhone ? `https://wa.me/${waPhone}` : '#';

  const resName = restaurantInfo?.name || 'Swat Capital Resort';
  const isOpen  = typeof isRestaurantCurrentlyOpen === 'function' && isRestaurantCurrentlyOpen();

  return (
    <div className="hp2">

      {/* HERO — full-bleed, negative margin cancels Layout pt-16/pt-20 */}
      <section className="hp2-hero -mt-16 md:-mt-20" id="home2-hero">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={i} src={src}
            alt={`${resName} — view ${i + 1}`}
            className={`hp2-slide${i === slideIndex ? ' hp2-slide-active' : ''}`}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        ))}
        <div className="hp2-hero-overlay" />

        <div className="hp2-hero-content">
          <div className="hp2-eyebrow">Swat Valley, Pakistan</div>
          <h1 className="hp2-hero-title">
            Where the<br />mountains<br /><em>begin.</em>
          </h1>
          <p className="hp2-hero-sub">
            {restaurantInfo?.description ||
              'A sanctuary above the clouds. Fine dining, mountain air, and moments that stay with you long after you leave.'}
          </p>
          <div className="hp2-hero-actions">
            <Link to="/reservations" className="hp2-btn-gold">Reserve a Table</Link>
            <Link to="/menu"         className="hp2-btn-ghost">Order Food →</Link>
          </div>
        </div>

        <div className="hp2-dots">
          {HERO_IMAGES.map((_, i) => (
            <button key={i} className={`hp2-dot${i === slideIndex ? ' hp2-dot-active' : ''}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => { goToSlide(i); restartTimer(); }}
            />
          ))}
        </div>

        {/* Scroll hint */}
        <div className="hp2-scroll-hint" aria-hidden="true">
          <div className="hp2-scroll-line" />
          <span>Scroll</span>
        </div>

      </section>

      {/* TICKER */}
      <div className="hp2-ticker" aria-hidden="true">
        <div className="hp2-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="hp2-ticker-item">{item}</span>
          ))}
        </div>
      </div>

      {/* 01 — THE STORY */}
      <section className="hp2-section-cream" id="home2-story">
        <div className="hp2-story-grid">
          <div className="hp2-ch-number hp2-reveal" aria-hidden="true">01</div>
          <div>
            <div className="hp2-ch-tag hp2-reveal hp2-d1">The Story</div>
            <h2 className="hp2-ch-heading hp2-reveal hp2-d2">
              A brothers' dream,<br /><em>the valley's pride</em>
            </h2>
            <p className="hp2-ch-body hp2-reveal hp2-d3">
              {resName} began as the private vision of three brothers — a small hilltop retreat for
              personal gatherings, perched above the city with a sweeping view of the entire Swat valley.
              Word spread. Visitors arrived. Requests for refreshments turned into something far greater.
            </p>
            <p className="hp2-ch-body hp2-reveal hp2-d4" style={{ marginTop:16 }}>
              What started as a personal escape became one of Swat's most celebrated destinations —
              a resort that draws travellers from across Pakistan, as much for its artistry and beauty
              as for its extraordinary hilltop setting.
            </p>
          </div>
        </div>
      </section>

      {/* 02 — THE EXPERIENCE */}
      <div className="hp2-dark-split">
        <div className="hp2-dark-img hp2-reveal">
          <img src={aboutImg} alt={`${resName} interior`} />
        </div>
        <div className="hp2-dark-content">
          <div className="hp2-ch-tag hp2-reveal" style={{ color:'var(--hp2-gold)' }}>02 — The Experience</div>
          <h2 className="hp2-dark-heading hp2-reveal hp2-d1">
            Where <em>luxury</em><br />meets raw nature.
          </h2>
          <div className="hp2-gold-rule hp2-reveal hp2-d2" />
          <p className="hp2-dark-body hp2-reveal hp2-d2">
            From the moment you arrive, {resName} envelops you in a world designed for comfort and
            contemplation. Halal certified, family friendly, and{' '}
            <span style={{ color: isOpen ? '#7EC882' : 'var(--hp2-gold)' }}>
              {isOpen ? 'open right now' : 'open daily'}
            </span>.{' '}{formatHours(operatingHours)}.
          </p>
          <div className="hp2-stats hp2-reveal hp2-d3">
            {[
              { num:'2,000+', label:'Happy guests'        },
              { num:'4.9',    label:'Guest rating'        },
              { num:'5',      label:'Years of excellence' },
            ].map(s => (
              <div key={s.label}>
                <div className="hp2-stat-num">{s.num}</div>
                <div className="hp2-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 03 — DINING */}
      <section className="hp2-section-cream" id="home2-dining">
        <div className="hp2-sec-header">
          <div>
            <div className="hp2-ch-tag hp2-reveal">03 — The Table</div>
            <h2 className="hp2-sec-title hp2-reveal hp2-d1">
              Where every meal<br />is a <em>ceremony.</em>
            </h2>
          </div>
          <Link to="/menu" className="hp2-sec-link hp2-reveal hp2-d2">View full menu</Link>
        </div>
        <div className="hp2-dining-grid">
          {DINING_CARDS.map((card, i) => (
            <Link to="/menu" key={card.title} className={`hp2-d-card hp2-reveal${i > 0 ? ` hp2-d${i}` : ''}`} style={{textDecoration:'none'}}>
              <img src={card.img} alt={card.title} loading="lazy" decoding="async" />
              <div className="hp2-d-overlay">
                <div className="hp2-d-cat">{card.cat}</div>
                <div className="hp2-d-title">{card.title}</div>
                <div className="hp2-d-desc">{card.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 04 — TESTIMONIALS */}
      <section className="hp2-section-black" id="home2-testimonials">
        <div className="hp2-center-header">
          <div className="hp2-ch-tag hp2-reveal" style={{ justifyContent:'center' }}>04 — Guest Voices</div>
          <h2 className="hp2-gallery-title hp2-reveal hp2-d1" style={{ textAlign:'center' }}>
            Words from our<br /><em>guests.</em>
          </h2>
        </div>
        <div className="hp2-test-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className={`hp2-test-card hp2-reveal${i > 0 ? ` hp2-d${i}` : ''}`}>
              <div className="hp2-test-quote">"</div>
              <div className="hp2-stars">{'★'.repeat(t.rating)}</div>
              <p className="hp2-test-comment">"{t.comment}"</p>
              <div className="hp2-test-reviewer">
                <div className="hp2-test-avatar">
                  <img src={t.img} alt={t.name} loading="lazy" decoding="async" />
                </div>
                <div>
                  <div className="hp2-test-name">{t.name}</div>
                  <div className="hp2-test-loc">{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 05 — GALLERY */}
      <section className="hp2-section-darkest" id="home2-gallery">
        <div className="hp2-gallery-header">
          <div>
            <div className="hp2-ch-tag" style={{ color:'var(--hp2-gold)' }}>05 — The View</div>
            <h2 className="hp2-gallery-title">Moments from<br /><em>the mountain.</em></h2>
          </div>
          <Link to="/gallery" className="hp2-gal-link">Full Gallery →</Link>
        </div>
        <div className="hp2-showcase">
          {/* Large featured image */}
          <div
            className="hp2-showcase-main"
            onTouchStart={e => { showcaseTouchRef.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (showcaseTouchRef.current === null) return;
              const dx = e.changedTouches[0].clientX - showcaseTouchRef.current;
              if (Math.abs(dx) > 40) setActiveGallery(g => dx < 0 ? (g + 1) % GALLERY_IMAGES.length : (g - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
              showcaseTouchRef.current = null;
            }}
          >
            <img
              src={GALLERY_IMAGES[activeGallery].src}
              alt={`${resName} gallery ${activeGallery + 1}`}
              className="hp2-showcase-main-img"
              key={activeGallery}
            />
            <div className="hp2-showcase-overlay">
              <span className="hp2-showcase-counter">{activeGallery + 1} / {GALLERY_IMAGES.length}</span>
            </div>
            {/* Prev / Next arrows */}
            <button className="hp2-showcase-arrow hp2-showcase-prev" onClick={() => setActiveGallery(g => (g - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length)}>&#8592;</button>
            <button className="hp2-showcase-arrow hp2-showcase-next" onClick={() => setActiveGallery(g => (g + 1) % GALLERY_IMAGES.length)}>&#8594;</button>
          </div>
          {/* Thumbnail strip */}
          <div className="hp2-showcase-thumbs">
            {GALLERY_IMAGES.map((img, i) => (
              <button
                key={i}
                className={`hp2-thumb${i === activeGallery ? ' hp2-thumb-active' : ''}`}
                onClick={() => setActiveGallery(i)}
              >
                <img src={img.src} alt={`Thumbnail ${i + 1}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 06 — TEAM (live from API) */}
      {(team.length > 0 || !teamLoaded) && <section className="hp2-section-cream" id="home2-team">
        <div className="hp2-sec-header">
          <div>
            <div className="hp2-ch-tag hp2-reveal">06 — The People</div>
            <h2 className="hp2-sec-title hp2-reveal hp2-d1">Faces behind<br />the <em>experience.</em></h2>
          </div>
        </div>
        <div className="hp2-team-grid">
          {!teamLoaded && (
            <p style={{ color:'var(--hp2-muted)', fontSize:13, gridColumn:'1/-1' }}>Loading team…</p>
          )}
          {teamLoaded && team.length === 0 && (
            <div className="hp2-team-empty">
              <p>Our team is coming soon.</p>
              <span>Team profiles are being added by management — check back shortly.</span>
            </div>
          )}
          {team.map(member => {
            const initials = member.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
            const photo = resolveUploadUrl(member.photoUrl);
            return (
              <div key={member._id} className="hp2-t-card hp2-reveal">
                <div className="hp2-t-img-wrap">
                  {photo && (
                    <img
                      className="hp2-t-img"
                      src={photo}
                      alt={member.name}
                      style={{ objectPosition: member.photoPosition || 'center' }}
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                  )}
                  <div className="hp2-t-initials" style={{ display: photo ? 'none' : 'flex' }}>
                    {initials}
                  </div>
                </div>
                <div className="hp2-t-name">{member.name}</div>
                <div className="hp2-t-role">{member.role}</div>
                {member.description && <p className="hp2-t-desc">{member.description}</p>}
              </div>
            );
          })}
        </div>
      </section>}

      {/* 07 — RESERVE */}
      <section className="hp2-reserve-section" id="home2-reserve">
        <div className="hp2-reserve-left">
          <div className="hp2-ch-tag hp2-reveal" style={{ color:'var(--hp2-gold)' }}>07 — The Table is Yours</div>
          <h2 className="hp2-reserve-heading hp2-reveal hp2-d1">Plan your<br /><em>escape.</em></h2>
          <p className="hp2-reserve-sub hp2-reveal hp2-d2">
            Whether it's a quiet dinner for two or a gathering of many — we're ready when you are.
            {restaurantInfo?.phone && (
              <> Call us on{' '}
                <a href={`tel:${String(restaurantInfo.phone).replace(/\s+/g,'')}`}
                   style={{ color:'var(--hp2-gold)', textDecoration:'none' }}>
                  {restaurantInfo.phone}
                </a>{' '}or fill in the form below.
              </>
            )}
          </p>

          <form className="hp2-r-form hp2-reveal hp2-d3" onSubmit={handleResSubmit}>
            <div className="hp2-r-row">
              <input placeholder="Your name"    required value={resForm.name}  onChange={patchForm('name')}  />
              <input placeholder="Phone number" required value={resForm.phone} onChange={patchForm('phone')} type="tel" />
            </div>
            <input placeholder="Email address" required value={resForm.email} onChange={patchForm('email')} type="email" />
            <div className="hp2-r-row">
              <input type="date" required value={resForm.date} onChange={patchForm('date')} min={new Date().toISOString().split('T')[0]} />
              <input type="time" required value={resForm.time} onChange={patchForm('time')} />
            </div>
            <div className="hp2-r-row">
              <select value={resForm.guests} onChange={patchForm('guests')}>
                <option value="" disabled>Number of guests</option>
                {['1','2','3','4','5','6','7','8'].map(n =>
                  <option key={n} value={n}>{n} guest{n !== '1' ? 's' : ''}</option>
                )}
                <option value="9">9+ guests (event)</option>
              </select>
              <select value={resForm.occasion} onChange={patchForm('occasion')}>
                <option value="">Occasion (optional)</option>
                <option>Casual Dinner</option>
                <option>Lunch</option>
                <option>Anniversary / Birthday</option>
                <option>Private Event</option>
                <option>Corporate</option>
              </select>
            </div>
            <button className="hp2-r-btn" type="submit" disabled={resLoading}>
              {resLoading ? 'Sending…' : 'Request Reservation'}
            </button>
          </form>

          <p className="hp2-reserve-alt hp2-reveal hp2-d4">
            Prefer to book online?{' '}
            <Link to="/reservations" style={{ color:'var(--hp2-gold)', textDecoration:'none' }}>
              Use our full booking page →
            </Link>
          </p>
        </div>

        <div className="hp2-reserve-right">
          <img src={hero2} alt={resName} />
          <div className="hp2-reserve-right-overlay">
            <span className="hp2-reserve-label">A table worth the journey.</span>
          </div>
        </div>
      </section>

      {/* CONTACT STRIP — all from context */}
      <div className="hp2-contact-strip">
        <div className="hp2-contact-inner">
          {restaurantInfo?.address && (
            <a
              href="https://www.google.com/maps/place/Swat+Capital+Resort/@34.7387884,72.3700245,17z/data=!3m1!4b1!4m6!3m5!1s0x38dc23cbf61604e7:0xbecb195e42dbf097!8m2!3d34.7387884!4d72.3700245!16s%2Fg%2F11ft5sbxxj"
              target="_blank" rel="noopener noreferrer"
              className="hp2-contact-item hp2-contact-link"
            >
              <span className="hp2-contact-label">Location</span>
              <span className="hp2-contact-val">{restaurantInfo.address} ↗</span>
            </a>
          )}
          {restaurantInfo?.phone && (
            <a href={`tel:${String(restaurantInfo.phone).replace(/\s+/g,'')}`} className="hp2-contact-item hp2-contact-link">
              <span className="hp2-contact-label">Phone</span>
              <span className="hp2-contact-val">{restaurantInfo.phone}</span>
            </a>
          )}
          {restaurantInfo?.email && (
            <a href={`mailto:${restaurantInfo.email}`} className="hp2-contact-item hp2-contact-link">
              <span className="hp2-contact-label">Email</span>
              <span className="hp2-contact-val">{restaurantInfo.email}</span>
            </a>
          )}
          {waPhone && (
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="hp2-contact-item hp2-contact-link">
              <span className="hp2-contact-label">WhatsApp</span>
              <span className="hp2-contact-val">Chat with us</span>
            </a>
          )}
          <div className="hp2-contact-item">
            <span className="hp2-contact-label">Hours</span>
            <span className="hp2-contact-val">{formatHours(operatingHours)}</span>
          </div>
          <div className="hp2-contact-item">
            <span className="hp2-contact-label">Status</span>
            <span className="hp2-contact-val" style={{ color: isOpen ? '#7EC882' : '#f87171' }}>
              {isOpen ? '● Open now' : '● Closed now'}
            </span>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp button */}
      {waPhone && (
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="hp2-wa-float"
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 999,
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'rgba(196,162,90,0.13)',
            border: '1px solid rgba(196,162,90,0.45)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 14px rgba(196,162,90,0.25), 0 4px 16px rgba(0,0,0,0.4)',
            transition: 'transform .2s, box-shadow .2s, background .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.background='rgba(196,162,90,0.25)'; e.currentTarget.style.boxShadow='0 0 22px rgba(196,162,90,0.5), 0 4px 20px rgba(0,0,0,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='scale(1)';   e.currentTarget.style.background='rgba(196,162,90,0.13)'; e.currentTarget.style.boxShadow='0 0 14px rgba(196,162,90,0.25), 0 4px 16px rgba(0,0,0,0.4)'; }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="#C4A25A">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}

      {/* SCOPED CSS — only active while this page is mounted */}
      <style>{`
        @media (max-width: 768px) { .hp2-wa-float { display: none !important; } }
        .hp2 {
          --hp2-serif:    'Cormorant Garamond', Georgia, serif;
          --hp2-sans:     'Inter', system-ui, sans-serif;
          --hp2-cream:    #0C0A07;
          --hp2-cream-mid:#221B0F;
          --hp2-black:    #0A0A0A;
          --hp2-gold:     #D4A030;
          --hp2-gold-l:   #E4BC48;
          --hp2-gold-dim: rgba(200,150,30,.18);
          --hp2-muted:    #A09080;
          font-family: var(--hp2-sans);
          background: var(--hp2-cream);
          color: #F5EDD5;
          overflow-x: hidden;
        }

        /* Prevent global max-width:100% from constraining full-bleed images */
        .hp2 img { max-width: none; }

        /* reveal */
        .hp2-reveal { opacity:0; transform:translateY(36px); transition:opacity .9s cubic-bezier(.22,1,.36,1), transform .9s cubic-bezier(.22,1,.36,1); }
        .hp2-reveal.hp2-visible { opacity:1; transform:translateY(0); }
        .hp2-d1{transition-delay:.10s} .hp2-d2{transition-delay:.22s} .hp2-d3{transition-delay:.38s} .hp2-d4{transition-delay:.52s}

        /* typography */
        .hp2-ch-tag { font-size:10px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:var(--hp2-gold); margin-bottom:18px; display:flex; align-items:center; gap:12px; }
        .hp2-ch-tag::before { content:''; width:30px; height:1px; background:var(--hp2-gold); flex-shrink:0; }
        .hp2-ch-heading { font-family:var(--hp2-serif); font-size:clamp(42px,5.5vw,72px); font-weight:300; line-height:1.06; margin-bottom:28px; letter-spacing:-.015em; }
        .hp2-ch-heading em { font-style:italic; }
        .hp2-ch-body { font-size:14px; line-height:1.95; color:var(--hp2-muted); max-width:440px; }
        .hp2-ch-number { font-family:var(--hp2-serif); font-size:220px; font-weight:300; color:rgba(180,140,60,.22); line-height:1; user-select:none; letter-spacing:-.05em; }
        .hp2-sec-title { font-family:var(--hp2-serif); font-size:clamp(34px,4.5vw,58px); font-weight:300; line-height:1.1; }
        .hp2-sec-title em { font-style:italic; }

        /* hero */
        .hp2-hero { position:relative; height:100vh; min-height:640px; overflow:hidden; display:flex; align-items:flex-end; padding:72px 52px; }
        .hp2-slide { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:50% 35%; opacity:0; transition:opacity 1.2s ease; }
        .hp2-slide-active { opacity:1; }
        .hp2-hero-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(0,0,0,.97) 0%,rgba(0,0,0,.78) 40%,rgba(0,0,0,.55) 100%),linear-gradient(to right,rgba(0,0,0,.82) 0%,rgba(0,0,0,0) 55%),linear-gradient(160deg,rgba(180,120,10,.10) 0%,transparent 60%); z-index:1; }
        .hp2-hero-content { position:relative; z-index:2; max-width:960px; }
        .hp2-eyebrow { font-size:11px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:var(--hp2-gold); margin-bottom:22px; display:flex; align-items:center; gap:14px; }
        .hp2-eyebrow::before { content:''; width:40px; height:1px; background:var(--hp2-gold); flex-shrink:0; }
        .hp2-hero-title { font-family:var(--hp2-serif); font-size:clamp(58px,9vw,120px); font-weight:300; line-height:.92; color:#fff; letter-spacing:-.02em; margin-bottom:36px; }
        .hp2-hero-title em { font-style:italic; color:var(--hp2-gold-l); }
        .hp2-hero-sub { font-size:14px; font-weight:300; letter-spacing:.04em; color:rgba(255,255,255,.55); max-width:420px; line-height:1.85; }
        .hp2-hero-actions { display:flex; align-items:center; gap:32px; margin-top:44px; }
        .hp2-btn-gold { background:linear-gradient(135deg,#B8841C,#D4A030 50%,#E4BC48); color:#0A0A0A; text-decoration:none; font-size:11px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; padding:17px 40px; transition:all .25s; box-shadow:0 4px 24px rgba(200,150,30,.35); }
        .hp2-btn-gold:hover { background:linear-gradient(135deg,#B8841C,#D4A030 50%,#E4BC48); box-shadow:0 6px 32px rgba(200,150,30,.55); transform:translateY(-1px); }
        .hp2-btn-ghost { background:rgba(255,255,255,.08); color:#fff; text-decoration:none; font-size:11px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; padding:17px 40px; border:1px solid rgba(255,255,255,.4); transition:all .25s; backdrop-filter:blur(8px); }
        .hp2-btn-ghost:hover { background:rgba(255,255,255,.18); border-color:rgba(255,255,255,.8); transform:translateY(-1px); }
        .hp2-dots { position:absolute; bottom:44px; right:52px; z-index:3; display:flex; flex-direction:column; gap:8px; }
        .hp2-dot { width:8px; height:8px; border-radius:50%; background:rgba(255,255,255,.35); border:none; cursor:pointer; transition:background .3s,transform .3s; padding:0; }
        .hp2-dot-active { background:var(--hp2-gold); transform:scale(1.3); }
        .hp2-scroll-hint { position:absolute; bottom:40px; left:52px; z-index:2; display:flex; flex-direction:column; align-items:center; gap:10px; }
        .hp2-scroll-hint span { font-size:9px; letter-spacing:.22em; text-transform:uppercase; color:rgba(255,255,255,.4); writing-mode:vertical-rl; }
        .hp2-scroll-line { width:1px; height:56px; background:linear-gradient(to bottom,rgba(255,255,255,.4),transparent); animation:hp2Pulse 2.2s ease-in-out infinite; }
        @keyframes hp2Pulse { 0%,100%{opacity:.4;transform:scaleY(1);transform-origin:top} 50%{opacity:.9;transform:scaleY(.5);transform-origin:top} }

        /* ticker */
        .hp2-ticker { background:linear-gradient(90deg,#B8841C,#D4A030 25%,#E4BC48 50%,#D4A030 75%,#B8841C); overflow:hidden; padding:13px 0; user-select:none; }
        .hp2-ticker-track { display:flex; animation:hp2Ticker 30s linear infinite; white-space:nowrap; width:max-content; }
        .hp2-ticker-item { font-size:10px; font-weight:700; letter-spacing:.22em; text-transform:uppercase; color:#0A0A0A; padding:0 36px; flex-shrink:0; }
        .hp2-ticker-item::after { content:'◆'; font-size:5px; margin-left:36px; opacity:.7; }
        @keyframes hp2Ticker { from{transform:translateX(0)} to{transform:translateX(-50%)} }

        /* sections */
        .hp2-section-cream { padding:130px 52px; position:relative;
          background:
            radial-gradient(ellipse 75% 55% at 50% 45%, rgba(170,115,15,0.09) 0%, transparent 68%),
            linear-gradient(180deg, #0A0A0A 0%, #141210 18%, #141210 82%, #0A0A0A 100%); }
        .hp2-section-cream::before,
        .hp2-section-cream::after { content:''; position:absolute; left:0; right:0; height:90px; pointer-events:none; z-index:1; }
        .hp2-section-cream::before { top:0; background:linear-gradient(to bottom, #0A0A0A, transparent); }
        .hp2-section-cream::after  { bottom:0; background:linear-gradient(to top, #0A0A0A, transparent); }
        .hp2-section-black { padding:120px 52px; background:var(--hp2-black); }
        .hp2-section-darkest { background:#0a0a0a; padding:120px 0; overflow:hidden; }
        .hp2-story-grid { max-width:1240px; margin:0 auto; display:grid; grid-template-columns:auto 1fr; gap:72px; align-items:center; }
        .hp2-sec-header { max-width:1240px; margin:0 auto 64px; display:flex; justify-content:space-between; align-items:flex-end; }
        .hp2-sec-link { font-size:11px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:var(--hp2-gold); text-decoration:none; display:flex; align-items:center; gap:8px; padding-bottom:3px; border-bottom:1px solid currentColor; transition:color .2s; white-space:nowrap; }
        .hp2-sec-link:hover { color:var(--hp2-gold); }
        .hp2-sec-link::after { content:'→'; }

        /* quote bleed */
        
        /* dark split */
        .hp2-dark-split { background:var(--hp2-black); display:grid; grid-template-columns:1fr 1fr; min-height:780px; }
        .hp2-dark-img { position:relative; overflow:hidden; }
        .hp2-dark-img img { width:100%; height:100%; object-fit:cover; object-position:center 20%; display:block; }
        .hp2-dark-img::after { content:''; position:absolute; top:0; bottom:0; right:0; width:42%; background:linear-gradient(to right, transparent 0%, rgba(13,13,13,.18) 28%, rgba(13,13,13,.60) 58%, rgba(13,13,13,.88) 80%, var(--hp2-black) 100%); pointer-events:none; z-index:2; }
        .hp2-dark-content { padding:100px 80px; display:flex; flex-direction:column; justify-content:center; }
        .hp2-dark-heading { font-family:var(--hp2-serif); font-size:clamp(30px,3.5vw,52px); font-weight:300; color:#fff; line-height:1.15; margin-bottom:28px; letter-spacing:-.01em; }
        .hp2-dark-heading em { font-style:italic; color:var(--hp2-gold-l); }
        .hp2-gold-rule { width:56px; height:1px; background:var(--hp2-gold); margin:28px 0; }
        .hp2-dark-body { font-size:14px; line-height:1.95; color:rgba(255,255,255,.55); max-width:400px; }
        .hp2-stats { display:flex; gap:52px; margin-top:56px; padding-top:40px; border-top:1px solid rgba(200,150,30,.2); }
        .hp2-stat-num { font-family:var(--hp2-serif); font-size:58px; font-weight:300; color:var(--hp2-gold-l); line-height:1; margin-bottom:6px; }
        .hp2-stat-label { font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:rgba(255,255,255,.42); }

        /* dining */
        .hp2-dining-grid { max-width:1240px; margin:0 auto; display:grid; grid-template-columns:repeat(3,1fr); gap:3px; }
        .hp2-d-card { position:relative; overflow:hidden; aspect-ratio:2/3; background:var(--hp2-cream-mid); cursor:pointer; }
        .hp2-d-card img { width:100%; height:100%; object-fit:cover; transition:transform .7s cubic-bezier(.22,1,.36,1); display:block; }
        .hp2-d-card:hover img { transform:scale(1.06); }
        .hp2-d-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(0,0,0,.78) 0%,rgba(0,0,0,0) 55%); display:flex; flex-direction:column; justify-content:flex-end; padding:32px 28px; }
        .hp2-d-cat { font-size:9px; letter-spacing:.2em; text-transform:uppercase; color:var(--hp2-gold); margin-bottom:6px; }
        .hp2-d-title { font-family:var(--hp2-serif); font-size:28px; font-weight:300; color:#fff; line-height:1.15; }
        .hp2-d-desc { font-size:12px; color:rgba(255,255,255,.55); line-height:1.65; margin-top:7px; }

        /* testimonials */
        .hp2-center-header { max-width:1240px; margin:0 auto 64px; text-align:center; }
        .hp2-test-grid { max-width:1240px; margin:0 auto; display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
        .hp2-test-card { background:rgba(255,255,255,.055); border:1px solid rgba(200,150,30,.18); padding:44px 34px 34px; position:relative; transition:border-color .35s,background .35s,transform .35s; }
        .hp2-test-card:hover { border-color:rgba(200,150,30,.45); background:rgba(255,255,255,.08); transform:translateY(-4px); }
        .hp2-test-quote { position:absolute; top:-20px; left:34px; width:40px; height:40px; background:linear-gradient(135deg,#B8841C,#E4BC48); display:flex; align-items:center; justify-content:center; font-family:var(--hp2-serif); font-size:24px; color:#0A0A0A; line-height:1; box-shadow:0 6px 20px rgba(200,150,30,.35); }
        .hp2-stars { color:var(--hp2-gold-l); font-size:14px; letter-spacing:3px; margin-bottom:22px; }
        .hp2-test-comment { font-family:var(--hp2-serif); font-size:19px; font-weight:300; font-style:italic; color:rgba(255,255,255,.88); line-height:1.68; margin-bottom:30px; }
        .hp2-test-reviewer { display:flex; align-items:center; gap:14px; padding-top:22px; border-top:1px solid rgba(200,150,30,.15); }
        .hp2-test-avatar { width:48px; height:48px; border-radius:50%; overflow:hidden; border:2px solid rgba(200,150,30,.6); flex-shrink:0; }
        .hp2-test-avatar img { width:100%; height:100%; object-fit:cover; }
        .hp2-test-name { font-family:var(--hp2-serif); font-size:19px; font-weight:400; color:#fff; margin-bottom:3px; }
        .hp2-test-loc { font-size:10px; letter-spacing:.16em; text-transform:uppercase; color:var(--hp2-gold-l); }

        /* gallery */
        .hp2-gallery-header { padding:0 52px; margin-bottom:40px; display:flex; justify-content:space-between; align-items:flex-end; }
        .hp2-gallery-title { font-family:var(--hp2-serif); font-size:clamp(34px,4.5vw,56px); font-weight:300; color:#fff; line-height:1.1; }
        .hp2-gallery-title em { font-style:italic; color:var(--hp2-gold-l); }
        .hp2-gal-link { font-size:11px; font-weight:500; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.5); text-decoration:none; transition:color .2s; }
        .hp2-gal-link:hover { color:var(--hp2-gold); }

        /* showcase gallery */
        .hp2-showcase { display:grid; grid-template-columns:1fr 280px; gap:0; height:80vh; min-height:560px; }
        .hp2-showcase-main { position:relative; overflow:hidden; background:#000; }
        .hp2-showcase-main-img { width:100%; height:100%; object-fit:cover; display:block; animation:hp2FadeIn .5s ease; }
        @keyframes hp2FadeIn { from { opacity:0; transform:scale(1.03); } to { opacity:1; transform:scale(1); } }
        .hp2-showcase-overlay { position:absolute; bottom:0; left:0; right:0; padding:24px 32px; background:linear-gradient(transparent,rgba(0,0,0,.6)); display:flex; justify-content:flex-end; pointer-events:none; }
        .hp2-showcase-counter { font-size:11px; letter-spacing:.18em; color:rgba(255,255,255,.5); text-transform:uppercase; }
        .hp2-showcase-arrow { position:absolute; top:50%; transform:translateY(-50%); background:rgba(0,0,0,.4); border:1px solid rgba(255,255,255,.15); color:#fff; width:52px; height:52px; font-size:20px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .2s,border-color .2s; backdrop-filter:blur(4px); }
        .hp2-showcase-arrow:hover { background:rgba(0,0,0,.7); border-color:var(--hp2-gold-l); box-shadow:0 0 16px rgba(200,150,30,.3); }
        .hp2-showcase-prev { left:24px; }
        .hp2-showcase-next { right:24px; }

        /* thumbnails */
        .hp2-showcase-thumbs { display:flex; flex-direction:column; gap:3px; overflow-y:auto; background:#0a0a0a; scrollbar-width:none; }
        .hp2-showcase-thumbs::-webkit-scrollbar { display:none; }
        .hp2-thumb { flex:1; min-height:100px; overflow:hidden; border:none; padding:0; cursor:pointer; position:relative; background:#000; }
        .hp2-thumb img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .4s ease,filter .4s ease; filter:brightness(.5) saturate(.6); }
        .hp2-thumb:hover img,.hp2-thumb-active img { filter:brightness(1) saturate(1); transform:scale(1.05); }
        .hp2-thumb-active::after { content:\'\'; position:absolute; inset:0; border:2px solid var(--hp2-gold); pointer-events:none; }

        /* team */
        .hp2-team-grid { max-width:1240px; margin:64px auto 0; display:grid; grid-template-columns:repeat(auto-fill,minmax(230px,1fr)); gap:36px; }
        .hp2-t-card { cursor:default; }
        .hp2-t-img-wrap { overflow:hidden; margin-bottom:18px; }
        .hp2-t-img { width:100%; aspect-ratio:3/4; object-fit:cover; display:block; transition:transform .5s cubic-bezier(.22,1,.36,1); }
        .hp2-t-card:hover .hp2-t-img { transform:scale(1.04); }
        .hp2-t-initials { width:100%; aspect-ratio:3/4; background:var(--hp2-cream-mid); display:flex; align-items:center; justify-content:center; font-family:var(--hp2-serif); font-size:52px; font-weight:300; color:var(--hp2-gold); }
        .hp2-t-name { font-family:var(--hp2-serif); font-size:23px; font-weight:400; margin-bottom:4px; }
        .hp2-t-role { font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:var(--hp2-gold); }
        .hp2-t-desc { font-size:13px; color:var(--hp2-muted); line-height:1.75; margin-top:10px; }
        .hp2-team-empty { grid-column:1/-1; padding:80px 48px; border:1px dashed rgba(200,150,30,.25); text-align:center; color:var(--hp2-muted); }
        .hp2-team-empty p { font-family:var(--hp2-serif); font-size:26px; font-weight:300; margin-bottom:8px; color:#F5EDD5; }

        /* reserve */
        .hp2-reserve-section { display:grid; grid-template-columns:1fr 1fr; min-height:700px; }
        .hp2-reserve-left { background:var(--hp2-black); padding:100px 68px; display:flex; flex-direction:column; justify-content:center; }
        .hp2-reserve-heading { font-family:var(--hp2-serif); font-size:clamp(34px,4vw,58px); font-weight:300; color:#fff; line-height:1.08; margin-bottom:14px; }
        .hp2-reserve-heading em { font-style:italic; color:var(--hp2-gold-l); }
        .hp2-reserve-sub { font-size:13px; line-height:1.9; color:rgba(255,255,255,.4); margin-bottom:40px; max-width:380px; }
        .hp2-reserve-alt { font-size:12px; color:rgba(255,255,255,.3); margin-top:18px; }
        .hp2-r-form { display:flex; flex-direction:column; gap:14px; }
        .hp2-r-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .hp2-r-form input,.hp2-r-form select { background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); color:#fff; padding:14px 18px; font-family:var(--hp2-sans); font-size:13px; outline:none; width:100%; transition:border-color .25s; -webkit-appearance:none; }
        .hp2-r-form input::placeholder { color:rgba(255,255,255,.3); }
        .hp2-r-form input:focus,.hp2-r-form select:focus { border-color:var(--hp2-gold); }
        .hp2-r-form select { color:rgba(255,255,255,.45); cursor:pointer; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23C4A25A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 16px center; padding-right:40px; }
        .hp2-r-form select option { background:#1a1a1a; color:#fff; }
        .hp2-r-btn { background:linear-gradient(135deg,#B8841C,#D4A030 50%,#E4BC48); color:#0A0A0A; border:none; padding:18px 44px; font-family:var(--hp2-sans); font-size:11px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; cursor:pointer; margin-top:8px; transition:all .25s; align-self:flex-start; box-shadow:0 4px 20px rgba(200,150,30,.3); }
        .hp2-r-btn:hover:not(:disabled) { background:linear-gradient(135deg,#B8841C,#D4A030 50%,#E4BC48); box-shadow:0 6px 28px rgba(200,150,30,.5); transform:translateY(-1px); }
        .hp2-r-btn:active { transform:scale(.98); }
        .hp2-r-btn:disabled { opacity:.6; cursor:not-allowed; }
        .hp2-reserve-right { position:relative; overflow:hidden; }
        .hp2-reserve-right img { width:100%; height:100%; object-fit:cover; object-position:center 30%; display:block; }
        .hp2-reserve-right::before { content:''; position:absolute; top:0; bottom:0; left:0; width:42%; background:linear-gradient(to left, transparent 0%, rgba(13,13,13,.18) 28%, rgba(13,13,13,.60) 58%, rgba(13,13,13,.88) 80%, var(--hp2-black) 100%); pointer-events:none; z-index:2; }
        .hp2-reserve-right-overlay { position:absolute; inset:0; display:flex; align-items:flex-end; padding:52px; background:linear-gradient(to top,rgba(0,0,0,.5) 0%,transparent 60%); z-index:3; }
        .hp2-reserve-label { font-family:var(--hp2-serif); font-size:16px; font-style:italic; color:rgba(255,255,255,.7); }

        /* contact strip */
        .hp2-contact-strip { background:#0E0C08; border-top:1px solid rgba(200,150,30,.15); padding:48px 52px; }
        .hp2-contact-inner { max-width:1240px; margin:0 auto; display:flex; flex-wrap:wrap; gap:40px; justify-content:space-between; }
        .hp2-contact-item { display:flex; flex-direction:column; gap:6px; min-width:160px; }
        .hp2-contact-link { text-decoration:none; transition:opacity .2s; }
        .hp2-contact-link:hover { opacity:.75; }
        .hp2-contact-label { font-size:9px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:var(--hp2-gold); }
        .hp2-contact-val { font-size:13px; color:rgba(255,255,255,.6); }

        /* responsive */
        @media (max-width:900px) {
          .hp2-hero { padding:48px 24px; height:88vh; min-height:540px; }
          .hp2-dots { right:24px; } .hp2-scroll-hint { left:24px; }
          .hp2-section-cream,.hp2-section-black { padding:80px 24px; }
          .hp2-story-grid { grid-template-columns:1fr; gap:32px; }
          .hp2-ch-number { display:none; }
          .hp2-dark-split { grid-template-columns:1fr; }
          .hp2-dark-img { min-height:300px; }
          .hp2-dark-content { padding:52px 32px; }
          .hp2-dining-grid { grid-template-columns:1fr; }
          .hp2-d-card { aspect-ratio:3/2; }
          .hp2-test-grid { grid-template-columns:1fr; }
          .hp2-sec-header { flex-direction:column; align-items:flex-start; gap:16px; }
          .hp2-reserve-section { grid-template-columns:1fr; }
          .hp2-reserve-right { min-height:300px; }
          .hp2-reserve-left { padding:60px 24px; }
          .hp2-gallery-header { padding:0 24px; margin-bottom:32px; }
          .hp2-showcase { grid-template-columns:1fr; height:auto; }
          .hp2-showcase-main { height:60vw; min-height:260px; }
          .hp2-showcase-thumbs { flex-direction:row; height:80px; overflow-x:auto; overflow-y:hidden; }
          .hp2-thumb { min-height:unset; min-width:80px; flex:0 0 80px; }
          .hp2-stats { gap:24px; flex-wrap:wrap; }
          .hp2-stat-num { font-size:40px; }
          .hp2-section-darkest { padding:80px 0; }
          .hp2-contact-strip { padding:40px 24px; }
          .hp2-contact-inner { gap:20px; }
          .hp2-r-row { grid-template-columns:1fr; }
        }
        /* Tablet refinement (additive, does not change phone or desktop layouts) */
        @media (min-width:600px) and (max-width:900px) {
          .hp2-dining-grid { grid-template-columns:1fr 1fr; }
          .hp2-test-grid { grid-template-columns:1fr 1fr; }
          .hp2-team-grid { grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); }
        }
      `}
      </style>

    </div>
  );
};

export default HomePage2;
